import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProcessTimeline from './components/ProcessTimeline';
import ProcessProducts from './components/ProcessProducts';
import FAQSection from './components/FAQSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const PROCESS_STEPS = [
  { id: 1, label: '철거', week: '1주차', color: '#c9a84c' },
  { id: 2, label: '설비/배관', week: '1~2주차', color: '#8b7355' },
  { id: 3, label: '목공·창호', week: '2~3주차', color: '#6b8e6b' },
  { id: 4, label: '전기/조명', week: '3주차', color: '#c9a84c' },
  { id: 5, label: '타일/바닥', week: '3~4주차', color: '#8b7355' },
  { id: 6, label: '도장/도배', week: '4주차', color: '#6b8e6b' },
  { id: 7, label: '가구/마감', week: '4~5주차', color: '#c9a84c' },
];

export default function InfoPage() {
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {
    document.title = '인테리어 공정 가이드 | 필그린 강남 인테리어 시공 절차';
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.content = content;
    };
    setMeta('description', '필그린 인테리어 공정 가이드. 철거→설비→목공→전기→타일→도장→마감 7단계 공정 일정과 자재 정보. 30평 기준 4~5주 완공.');
    setMeta('og:title', '인테리어 공정 가이드 | 필그린 강남 인테리어 시공 절차', true);
    setMeta('og:url', `${SITE_URL}/info`, true);

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: '인테리어 공정 가이드 - 필그린',
      description: '30평 기준 4~5주 공정 일정과 각 단계별 필요한 자재 및 상품 정보를 한눈에 확인하세요.',
      totalTime: 'P5W',
      step: PROCESS_STEPS.map((s) => ({
        '@type': 'HowToStep',
        name: s.label,
        text: `${s.label} 공정 (${s.week})`,
        position: s.id,
      })),
      provider: {
        '@type': 'LocalBusiness',
        name: '필그린 인테리어',
        url: SITE_URL,
      },
    };

    const scriptId = 'jsonld-info';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = scriptId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);
    return () => { document.getElementById(scriptId)?.remove(); };
  }, []);

  return (
    <div className="bg-[#f7f5f3] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://storage.readdy-site.link/project_files/0477f867-7633-4e4c-8292-18c2b50a08d2/c417aa41-fc84-46e6-9709-4f9e15c12d1b_unnamed.jpg?v=8130b9857c58ef05388c818e0de13a20')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-[#f7f5f3]" />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <span
            className="inline-block text-xs font-semibold tracking-[0.3em] uppercase mb-4"
            style={{ color: '#c9a84c' }}
          >
            Interior Information
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-5 md:mb-6 leading-snug md:leading-tight">
            인테리어 공정 가이드
          </h1>
          <p className="text-white/70 text-sm md:text-lg max-w-3xl mx-auto leading-relaxed break-keep">
            30평 기준 4~5주 공정 일정과<br />
            각 단계별 자재 및 상품 정보를 한눈에 확인하세요.<br />
            필그린이 제안하는 체계적인 인테리어 공정 프로세스입니다.
          </p>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 pb-16">
        <ProcessTimeline
          steps={PROCESS_STEPS}
          activeStep={activeStep}
          onStepClick={setActiveStep}
        />
      </section>

      {/* Process Products */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 pb-24">
        <ProcessProducts activeStep={activeStep} steps={PROCESS_STEPS} />
      </section>

      {/* Divider */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 mb-16">
        <div className="h-px" style={{ background: 'linear-gradient(to right, transparent, #c9a84c33, transparent)' }} />
      </div>

      {/* FAQ Section */}
      <FAQSection />

      <Footer />
    </div>
  );
}
